import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { GoogleAuthService, GoogleButtonOptions } from '../../services/google-auth.service';
import { LoggerService } from '../../services/logger.service';

/**
 * Google's own sign-in button (GIS) with the "oder" divider above it. Renders nothing
 * when Google is unavailable (blocked script, no client id), so the page's password
 * form always keeps working.
 */
@Component({
  selector: 'app-google-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div [hidden]="!available()" class="flex flex-col gap-4">
      <div
        class="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
        <span class="bg-card text-muted-foreground relative z-10 px-2">oder</span>
      </div>
      <!-- Has to stay visible and unobscured: behind an overlay Chrome rejects the
           FedCM credential request, so the click would do nothing. -->
      <div #button class="flex justify-center [&>*]:max-w-full"></div>
    </div>
  `,
})
export class GoogleButtonComponent implements OnDestroy {
  private readonly google = inject(GoogleAuthService);
  private readonly logger = inject(LoggerService);
  private readonly button = viewChild.required<ElementRef<HTMLElement>>('button');
  private dispose: (() => void) | null = null;

  readonly text = input<GoogleButtonOptions['text']>('signin_with');
  /** The Google ID token ("credential") after the user picked an account. */
  readonly credential = output<string>();
  readonly available = signal(false);

  constructor() {
    afterNextRender(() => this.render());
  }

  private async render(): Promise<void> {
    try {
      this.dispose = await this.google.renderButton(
        this.button().nativeElement,
        { text: this.text() },
        (credential) => this.credential.emit(credential),
      );
      this.available.set(true);
    } catch (error) {
      this.logger.error('Google Sign-In not available:', error);
    }
  }

  ngOnDestroy(): void {
    this.dispose?.();
  }
}
